import { useState, useEffect } from 'react';
import { logger } from '@src/utils/logger';

type GeolocationResult = {
  isAU: boolean;
  isNorthAmerica: boolean;
  countryCode: string | null;
  isLoading: boolean;
  error: Error | null;
};

// Country code mapping for common country names
const COUNTRY_CODE_MAP: Record<string, string> = {
  'AU': 'AU',
  'AUSTRALIA': 'AU',
  'US': 'US',
  'USA': 'US',
  'UNITED STATES': 'US',
  'UNITED STATES OF AMERICA': 'US',
  'CA': 'CA',
  'CANADA': 'CA',
  'MX': 'MX',
  'MEXICO': 'MX',
};

// Get country code from country name or code
function getCountryCode(country: string | null | undefined): string | null {
  if (!country) return null;
  const upperCountry = country.toUpperCase().trim();
  return COUNTRY_CODE_MAP[upperCountry] || upperCountry;
}

export const useGeolocation = (): GeolocationResult => {
  const [isAU, setIsAU] = useState(false);
  const [isNorthAmerica, setIsNorthAmerica] = useState(false);
  const [countryCode, setCountryCode] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const detectLocation = async () => {
      try {
        // Use IP-based geolocation API
        // This endpoint(https://dev.openparachute.dev/geo) should return: { country: "AU", country_code: null, ... }
        const response = await fetch('/geo', {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
            },
          });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        
        // Extract country code from country field (since country_code may be null)
        const country = data.country || data.country_code;
        const detectedCountryCode = getCountryCode(country);

        if (!detectedCountryCode) {
          throw new Error('Country code not found in geolocation response');
        }

        setCountryCode(detectedCountryCode);
        
        // Check if user is in Australia
        const isAustralia = detectedCountryCode === 'AU';
        setIsAU(isAustralia);
        
        // Check if user is in North America (US, CA, MX)
        const northAmericaCountries = ['US', 'CA', 'MX'];
        const isNA = northAmericaCountries.includes(detectedCountryCode);
        setIsNorthAmerica(isNA);

        setIsLoading(false);
        logger.debug('[Geolocation] Location detected:', { 
          countryCode: detectedCountryCode,
          country: data.country,
        });

      } catch (error) {
        logger.error('[Geolocation] Error detecting location:', error);
        setError(error as Error);
        setIsLoading(false);
        // App continues with default values (both false)
      }
    };

    detectLocation();
  }, []);

  return {
    isAU,
    isNorthAmerica,
    countryCode,
    isLoading,
    error,
  };
};